import type { UIMessage } from 'ai';

export type AskThreadSummary = {
  id: string;
  title: string;
  pinnedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
};

export type AskThreadGroup = {
  key: 'pinned' | 'today' | 'yesterday' | 'earlier';
  count: number;
  items: AskThreadSummary[];
};

export type AskThreadDetail = AskThreadSummary & {
  messages: Array<{
    id: string;
    role: string;
    parts: unknown[];
    toolNames: string[];
    createdAt: string;
  }>;
};

// Thread CRUD + selection state for /ask. Single composable so the sidebar
// (list, pin, delete) and the main column (load on click, rename inline) can
// share one reactive source of truth.
export const useAskThreads = () => {
  const authStore = useAuthStore();
  const threads = useState<AskThreadSummary[]>('ask:threads', () => []);
  const activeThreadId = useState<string | null>('ask:active-thread-id', () => null);
  const isLoading = ref(false);

  const authHeaders = () => (authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {});

  const create = async (title?: string): Promise<AskThreadSummary | null> => {
    try {
      const res = await $fetch<{ success: boolean; data: AskThreadSummary }>('/api/ai/threads', {
        method: 'POST',
        headers: authHeaders(),
        body: { title },
      });
      if (res?.data) {
        threads.value = [res.data, ...threads.value];
        return res.data;
      }
    } catch (err) {
      console.error('[useAskThreads] create failed', err);
    }
    return null;
  };

  const refresh = async () => {
    isLoading.value = true;
    try {
      const res = await $fetch<{ success: boolean; data: AskThreadSummary[] }>('/api/ai/threads', {
        headers: authHeaders(),
      });
      threads.value = res?.data ?? [];
    } catch (err) {
      console.error('[useAskThreads] refresh failed', err);
      threads.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const loadThread = async (id: string): Promise<AskThreadDetail | null> => {
    try {
      const res = await $fetch<{ success: boolean; data: AskThreadDetail }>(`/api/ai/threads/${id}`, {
        headers: authHeaders(),
      });
      return res?.data ?? null;
    } catch {
      return null;
    }
  };

  const rename = async (id: string, title: string) => {
    await $fetch(`/api/ai/threads/${id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: { title },
    });
    await refresh();
  };

  const togglePin = async (thread: AskThreadSummary) => {
    await $fetch(`/api/ai/threads/${thread.id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: { pin: !thread.pinnedAt },
    });
    await refresh();
  };

  const remove = async (id: string) => {
    await $fetch(`/api/ai/threads/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (activeThreadId.value === id) activeThreadId.value = null;
    await refresh();
  };

  // Group threads by Today / Yesterday / Earlier / Pinned for the sidebar.
  const grouped = computed(() => {
    const pinned: AskThreadSummary[] = [];
    const today: AskThreadSummary[] = [];
    const yesterday: AskThreadSummary[] = [];
    const earlier: AskThreadSummary[] = [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;

    for (const t of threads.value) {
      if (t.pinnedAt) {
        pinned.push(t);
        continue;
      }
      const ts = new Date(t.updatedAt).getTime();
      if (ts >= startOfToday) today.push(t);
      else if (ts >= startOfYesterday) yesterday.push(t);
      else earlier.push(t);
    }

    // No display labels here — HistorySidebar translates `key` (+ `count`) at render.
    const groups: AskThreadGroup[] = [
      { key: 'pinned', count: pinned.length, items: pinned },
      { key: 'today', count: today.length, items: today },
      { key: 'yesterday', count: yesterday.length, items: yesterday },
      { key: 'earlier', count: earlier.length, items: earlier },
    ];
    return groups.filter((g) => g.items.length > 0);
  });

  return {
    threads,
    grouped,
    activeThreadId,
    isLoading,
    create,
    refresh,
    loadThread,
    rename,
    togglePin,
    remove,
  };
};

// Helper to extract the first text part from a UIMessage — used by the sidebar
// to show a preview of the user's question alongside the auto-generated title.
export const firstTextOf = (msg: UIMessage): string => {
  if (!msg?.parts) return '';
  for (const part of msg.parts) {
    if (part.type === 'text' && 'text' in part && typeof part.text === 'string') {
      return part.text;
    }
  }
  return '';
};
